import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IOptions } from '../../../common/common.types'
import { IImmobilizationState } from '../../procedures/immobilization/store/immobilization.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateImmobilizationProcedureProps {
  procedureEvent: IEKGEvent
  immobilizationOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface ImmobilizationProcedureObject {
  immobilizationState: IImmobilizationState
  timelineItem: IItem
}

const createImmobilizationProcedure = (props: CreateImmobilizationProcedureProps): ImmobilizationProcedureObject => {
  const { procedureEvent, immobilizationOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const itemComments = procedureEvent.procedureComments ?? ''
  const immobilizationDevicesUsedOption = immobilizationOptions.find(
    (option) => (option.id ?? '').toString() === procedureEvent.procedureAction
  )
  const immobilizationDevicesUsed = immobilizationDevicesUsedOption ? [immobilizationDevicesUsedOption] : []

  const itemSubHeader = `By , Option: , Devices Used: ${
    immobilizationDevicesUsedOption?.name ?? ''
  }, Assessment: , Site: , Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.IMMOBILIZATION_PROCEDURE,
    null,
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Immobilization',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_IMMOBILIZATION_PROCEDURE_ITEM,
    defaultHref: TabConstants.IMMOBILIZATION_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let immobilizationState: IImmobilizationState = {
    key: clientEventId,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    option: null,
    devicesUsed: immobilizationDevicesUsed,
    assessment: [],
    site: [],
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: false,
    updateInputRows: false,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Immobilization',
    size: '',
    rolePerforming: null,
    rolePerformingPNNV: null,
    isEkgImport: 1,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    // When mutliple actions(devicesUsed) are selected, "null" is used to match the behavior of the WEB implementation.
    // If only one action is selected, its name is set as the value.
    immobilizationState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: immobilizationState,
      actionField: (s) => (s.devicesUsed?.length >= 1 ? s.devicesUsed[0].name : null),
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    immobilizationState = handleNemsisCodeError(error, immobilizationState)
  }

  const newImmobilizationProcedureObject: ImmobilizationProcedureObject = {
    immobilizationState,
    timelineItem,
  }

  return newImmobilizationProcedureObject
}

export default createImmobilizationProcedure
